import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { LDAP_MANAGEMENT, TITLES } from 'locales/locales'
import {
  useLazyGetLdapSyncServerListAdminPanelQuery,
  useCreateAndUpdateLdapUsersMutation,
  useCreateAndUpdateLdapRolesMutation,
  useCreateAndUpdateLdapGroupsMutation,
  useCreateAndUpdateLdapRoomsMutation,
  useCreateAndUpdateLdapSubscriptionsMutation,
  useSendLdapSyncLogMutation,
  useStartLdapSyncMutation,
  useStopLdapSyncMutation,
} from 'app/services/system.service'

import { GridColumn } from '@progress/kendo-react-grid'
import { useAppSelector } from '../../../../app/hooks'
import { IRolePermissions, SettingsPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import KGrid from 'components/kendo/grid/KGrid'
import { FixMeLater } from 'types'
import { formatDate } from '@progress/kendo-intl'

interface LdapSyncLogItem {
  type: string
  details: {
    action: string
    syncDate: string | number | Date
  }
  createdAt: string | number | Date
}

interface DataStateFilter {
  field: string
  operator: string
  value: boolean | string
}

interface DataState {
  take: number
  skip: number
  filter: {
    logic: string
    filters: DataStateFilter[]
  }
  sort?: Array<{ field: string; dir: string }>
}

const initialDataState: DataState = {
  take: 10,
  skip: 0,
  filter: {
    logic: 'and',
    filters: [],
  },
  sort: [],
}

interface ILogData {
  type: string
  details: {
    action: string
    syncDate: Date
  }
  createdAt: Date
}

const AdminLDAPSettings: React.FC = () => {
  const { t } = useTranslation()
  const [sendLdapSyncLog] = useSendLdapSyncLogMutation()
  const [getLdapSyncList] = useLazyGetLdapSyncServerListAdminPanelQuery()
  const [createAndUpdateLdapUsers] = useCreateAndUpdateLdapUsersMutation()
  const [createAndUpdateLdapRoles] = useCreateAndUpdateLdapRolesMutation()
  const [createAndUpdateLdapGroups] = useCreateAndUpdateLdapGroupsMutation()
  const [createAndUpdateLdapRooms] = useCreateAndUpdateLdapRoomsMutation()
  const [createAndUpdateLdapSubscriptions] = useCreateAndUpdateLdapSubscriptionsMutation()
  const [startLdapSync] = useStartLdapSyncMutation()
  const [stopLdapSync] = useStopLdapSyncMutation()

  const [loading, setLoading] = useState(false)
  const [currentData, setCurrentData] = useState<{ data: LdapSyncLogItem[]; totalCount: number }>()
  const [dataState, setDataState] = useState<DataState>(initialDataState)

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const notificationLanguage = useAppSelector<any>((state) => state.auth.user.notificationLanguage)

  const hasLdapUpdatePermission = isFlagSetIn(
    [SettingsPermissions.UPDATE_LDAP_SETTINGS],
    permissions.settings,
  )

  useEffect(() => {
    fetchLdapSync()
  }, [dataState])

  const createAndSendLog = async (action: string, syncType: string) => {
    const logData: ILogData = {
      type: syncType,
      details: {
        action: action,
        syncDate: new Date(),
      },
      createdAt: new Date(),
    }

    await sendLdapSyncLog(logData)
  }

  const fetchLdapSync = async () => {
    try {
      const response = await getLdapSyncList({ ...dataState, count: true })
      if (response.data.success) {
        const formattedData: LdapSyncLogItem[] = response.data.data.map(
          (item: LdapSyncLogItem) => ({
            ...item,
            details: {
              ...item.details,
              syncDate: new Date(item?.details?.syncDate || '').toLocaleString(),
            },
          }),
        )
        setCurrentData({ data: formattedData, totalCount: response.data.totalCount })
      }
    } catch (e) {
      console.warn('ERROR', e)
    }
  }

  const handleLdapOperation = async (
    operation: () => Promise<{ data: FixMeLater } | { error: FixMeLater }>,
    logType: string,
    actionType: string,
  ) => {
    setLoading(true)
    try {
      await operation()
      await createAndSendLog(actionType, logType)
      await fetchLdapSync()
    } catch (error) {
      console.error('LDAP Server senkronizasyonu hatası:', error)
    } finally {
      setLoading(false)
    }
  }

  const syncTypeSwitch: FixMeLater = (type: string) => {
    switch (type) {
      case 'start':
        return t(LDAP_MANAGEMENT.START_SYNC)
      case 'stop':
        return t(LDAP_MANAGEMENT.STOP_SYNC)
      case 'groups':
        return t(LDAP_MANAGEMENT.GROUP_SYNC)
      case 'user':
        return t(LDAP_MANAGEMENT.USER_SYNC)
      case 'roles':
        return t(LDAP_MANAGEMENT.ROLE_SYNC)
      case 'rooms':
        return t(LDAP_MANAGEMENT.ROOM_SYNC)
      case 'subscriptions':
        return t(LDAP_MANAGEMENT.SUB_SYNC)
      default:
        return t(LDAP_MANAGEMENT.UNKNOWING_SYNC)
    }
  }

  const handleFilterChange = useCallback(
    (event: FixMeLater) => {
      setDataState((prev) => ({
        ...prev,
        filter: event.filter,
      }))
    },
    [dataState],
  )

  const onDataStateChange = useCallback(
    (event: FixMeLater) => {
      setDataState((prev) => ({
        ...prev,
        skip: event.dataState.skip,
        take: event.dataState.take,
        sort: event.dataState.sort,
      }))
    },
    [dataState],
  )

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.LDAP_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        {loading && (
          <SpinnerOverlay>
            <Spinner></Spinner>
            <SpinnerText>{t(LDAP_MANAGEMENT.UPDATING_LDAP)}</SpinnerText>
          </SpinnerOverlay>
        )}

        {hasLdapUpdatePermission ? (
          <ButtonContainer>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => startLdapSync({}), 'start', 'Functioning Consistently')
              }
            >
              {t(LDAP_MANAGEMENT.START_SYNC)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => stopLdapSync({}), 'stop', 'Functioning Consistently')
              }
            >
              {t(LDAP_MANAGEMENT.STOP_SYNC)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => createAndUpdateLdapUsers({}), 'user', 'Worked Once')
              }
            >
              {t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_USERS)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => createAndUpdateLdapRoles({}), 'roles', 'Worked Once')
              }
            >
              {t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_ROLES)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => createAndUpdateLdapGroups({}), 'groups', 'Worked Once')
              }
            >
              {t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_GROUPS)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(() => createAndUpdateLdapRooms({}), 'rooms', 'Worked Once')
              }
            >
              {t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_ROOMS)}
            </LdapButton>
            <LdapButton
              onClick={() =>
                handleLdapOperation(
                  () => createAndUpdateLdapSubscriptions({}),
                  'subscriptions',
                  'Worked Once',
                )
              }
            >
              {t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_SUBSCRIPTIONS)}
            </LdapButton>
          </ButtonContainer>
        ) : null}
        <GridContainer>
          <KGrid
            datasource={currentData?.data || []}
            pageable={true}
            total={currentData?.totalCount || 0}
            dataState={dataState}
            sortable={true}
            filterable={false}
            editMode={false}
            onDataStateChange={onDataStateChange}
            onFilterChange={handleFilterChange}
            style={{ height: '500px' }}
          >
            <GridColumn
              field="type"
              title={t(LDAP_MANAGEMENT.TYPE)}
              cell={(props) => <td>{syncTypeSwitch(props.dataItem.type)}</td>}
            />
            <GridColumn
              field="action"
              title={t(LDAP_MANAGEMENT.ACTION)}
              cell={(props) => (
                <td>
                  {props.dataItem?.details?.action === 'Functioning Consistently'
                    ? t(LDAP_MANAGEMENT.FUNCTIONING_CONSISTENTLY)
                    : t(LDAP_MANAGEMENT.WORKED_ONCE)}
                </td>
              )}
            />
            <GridColumn
              field="createdAt"
              sortable={true}
              filterable={false}
              title={t(LDAP_MANAGEMENT.SYNCHRONIZATION_DATE)}
              cell={(props) => (
                <td>
                  {formatDate(new Date(props?.dataItem?.createdAt), 'dd/MM/yyyy HH:mm:ss', "en-US")}
                </td>
              )}
            />
          </KGrid>
        </GridContainer>
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminLDAPSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
  position: relative;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`

const LdapButton = styled.button`
  background-color: #fff;
  color: #1c1c1c;
  padding: 13px 20px;
  font-weight: 600;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  cursor: pointer;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const GridContainer = styled.div`
  border-top: 1px solid #e6e6e6;
  margin-top: 20px;
`

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const SpinnerText = styled.div`
  margin-top: 15px;
  font-size: 16px;
  color: #333;
`
